import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '../i18n/useTranslation';

const GOLD = '#C9A84C';

export default function FDAPermissionPrompt() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const hasSetup = useRef(false);
  const unlisteners = useRef([]);

  const dismiss = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (hasSetup.current) return;
    hasSetup.current = true;

    const tauri = window.__TAURI__;
    if (!tauri?.event?.listen) return;

    const listen = tauri.event.listen.bind(tauri.event);

    const setup = async () => {
      unlisteners.current.push(await listen('fda-permission-required', () => {
        setVisible(true);
      }));

      unlisteners.current.push(await listen('fda-permission-granted', () => {
        setVisible(false);
      }));
    };

    setup();

    const onPlayFda = () => setVisible(true);
    window.addEventListener('maestro:fda-required', onPlayFda);

    return () => {
      unlisteners.current.forEach(fn => fn());
      window.removeEventListener('maestro:fda-required', onPlayFda);
    };
  }, []);

  const handleOpenSettings = useCallback(async () => {
    try {
      const { invoke } = window.__TAURI__?.core || window.__TAURI__ || {};
      if (invoke) await invoke('open_fda_settings');
    } catch (e) {
      console.error('[FDA] Failed to open settings:', e);
    }
  }, []);

  const handleRecheck = useCallback(async () => {
    try {
      const { invoke } = window.__TAURI__?.core || window.__TAURI__ || {};
      if (!invoke) return;
      const granted = await invoke('recheck_fda_permission');
      if (granted) setVisible(false);
    } catch (e) {
      console.error('[FDA] Recheck failed:', e);
    }
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: '#1a1a1a', borderRadius: 16, padding: '32px 36px',
        maxWidth: 440, width: '90%', position: 'relative',
        border: `1px solid ${GOLD}33`, boxShadow: `0 8px 32px rgba(0,0,0,0.5)`,
      }}>
        {/* Dismiss X */}
        <button
          onClick={dismiss}
          style={{
            position: 'absolute', top: 12, right: 14,
            background: 'none', border: 'none', color: '#888',
            fontSize: 20, cursor: 'pointer', lineHeight: 1,
          }}
          aria-label="Close"
        >
          &times;
        </button>

        {/* Icon */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, textAlign: 'center', margin: '0 0 12px' }}>
          {t('fda.title')}
        </h2>

        <p style={{ color: '#aaa', fontSize: 14, lineHeight: 1.6, textAlign: 'center', margin: '0 0 24px' }}>
          {t('fda.play_description') || t('fda.description')}
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleOpenSettings}
            style={{
              background: GOLD, color: '#000', border: 'none',
              borderRadius: 8, padding: '10px 20px', fontSize: 14,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            {t('fda.open_settings')}
          </button>
          <button
            onClick={handleRecheck}
            style={{
              background: 'transparent', color: GOLD,
              border: `1px solid ${GOLD}`, borderRadius: 8,
              padding: '10px 20px', fontSize: 14,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            {t('fda.recheck')}
          </button>
        </div>
      </div>
    </div>
  );
}
